/**
 * ProviderPermissionsTab Component
 * 
 * Displays the provider's Healthie organization membership and permissions
 * Shows their role, permission template, and specific capabilities
 */
import React from 'react';
import { useRecordContext } from 'react-admin';
import { Box, Typography, Grid, Paper, Alert, Chip } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import PersonIcon from '@mui/icons-material/Person';

const ProviderPermissionsTab = () => {
    const record = useRecordContext();
    const healthieData = record?.healthie_data;
    const orgMembership = healthieData?.organization_membership;

    if (!orgMembership) {
        return (
            <Box sx={{ p: 2 }}>
                <Alert severity="warning" icon={<InfoOutlinedIcon />}>
                    <Typography variant="body2">
                        <strong>No Healthie Organization Membership</strong>
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        This provider is not a member of a Healthie organization.
                    </Typography>
                </Alert>
            </Box>
        );
    }

    const permissionItems = [
        {
            label: 'Can See Clients',
            value: orgMembership.can_see_clients,
            description: 'Can view client profiles and information'
        },
        {
            label: 'Can Charge Clients',
            value: orgMembership.can_charge_clients,
            description: 'Can create and process charges for clients'
        },
        {
            label: 'Can Edit Appointment Types',
            value: orgMembership.can_edit_appointment_types,
            description: 'Can create and modify appointment type settings'
        }
    ];

    return (
        <Box sx={{ p: 2 }}>
            {/* Role Information */}
            <Paper elevation={1} sx={{ p: 2, mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    {orgMembership.is_admin ? (
                        <AdminPanelSettingsIcon color="error" />
                    ) : (
                        <PersonIcon color="primary" />
                    )}
                    <Typography variant="h6">
                        Healthie Role
                    </Typography>
                </Box>

                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                        <Box>
                            <Typography variant="caption" color="text.secondary">
                                Role Type
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                                {orgMembership.is_admin && (
                                    <Chip
                                        label="Admin"
                                        color="error"
                                        size="small"
                                        icon={<AdminPanelSettingsIcon />}
                                    />
                                )}
                                {orgMembership.is_provider && (
                                    <Chip
                                        label="Provider"
                                        color="primary"
                                        size="small"
                                        icon={<PersonIcon />}
                                    />
                                )}
                            </Box>
                        </Box>
                    </Grid>

                    {orgMembership.permission_template && (
                        <Grid item xs={12} sm={6}>
                            <Box>
                                <Typography variant="caption" color="text.secondary">
                                    Permission Template
                                </Typography>
                                <Typography variant="body2" sx={{ mt: 0.5 }}>
                                    {orgMembership.permission_template.name}
                                </Typography>
                            </Box>
                        </Grid>
                    )}
                </Grid>
            </Paper>

            {/* Specific Permissions */}
            <Typography variant="h6" sx={{ mb: 2 }}>
                Specific Permissions
            </Typography>

            <Grid container spacing={2}>
                {permissionItems.map((item, index) => (
                    <Grid item xs={12} md={6} key={index}>
                        <Paper
                            elevation={1}
                            sx={{
                                p: 2,
                                border: '1px solid',
                                borderColor: item.value ? 'success.light' : 'grey.300',
                                bgcolor: item.value ? 'success.50' : 'grey.50'
                            }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                                {item.value ? (
                                    <CheckCircleIcon color="success" sx={{ mt: 0.5 }} />
                                ) : (
                                    <CancelIcon color="disabled" sx={{ mt: 0.5 }} />
                                )}
                                <Box>
                                    <Typography variant="body1" fontWeight={600}>
                                        {item.label}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        {item.description}
                                    </Typography>
                                </Box>
                            </Box>
                        </Paper>
                    </Grid>
                ))}
            </Grid>

            <Alert severity="info" sx={{ mt: 3 }}>
                <Typography variant="caption">
                    <strong>Note:</strong> These are Healthie EHR permissions only. 
                    Admin Panel permissions are managed separately via the Permissions tab.
                </Typography>
            </Alert>
        </Box>
    );
};

export default ProviderPermissionsTab;

